import EnvConfig from 'shared/libraries/mwc/envConfig';
import { BackendError } from "shared/libraries/backend/BackendError";
import { BackendModel } from "shared/libraries/backend/BackendModel";
import { ProgressCallback } from "shared/controllers/UserDataManager";
import { getToken } from "shared/libraries/auth";
import { NotificationModel, TNotificationType, TNotificationCallback } from "shared/libraries/backend/Notification";
import RenderJob, { TRenderStatus } from './RenderJob';

export type TVirtualTourRequest = {
    project: number,
    camera_count: number,
}

export type TVirtualTour = {
    id: number,
    project: number,
    status?: TRenderStatus,
    json_upload_url?: string,
    progress: number,
    published_at: string,
    created_at: string
}

export class VirtualTourModel extends BackendModel implements RenderJob {
    static path: string = `${BackendModel.base}/vt/`;
    readonly data: TVirtualTour;
    constructor(data: TVirtualTour) {
        super();
        this.data = data;
    }
    static async create(project: number, cameraCount: number) : Promise<VirtualTourModel> {
        const response : TVirtualTour = await super.post<TVirtualTourRequest, TVirtualTour>(VirtualTourModel.path, {project: project, camera_count: cameraCount});
        const instance : VirtualTourModel = new VirtualTourModel(response);
        return instance;
    }
    static async find(project: number, id: number) : Promise<VirtualTourModel> {
        const path : string = `${BackendModel.base}/project/${project}/vt/${id}/`;
        const response : TVirtualTour = await super.get<TVirtualTour>(path);
        const instance : VirtualTourModel = new VirtualTourModel(response);
        return instance;
    }
    static async remove(project: number, id: number) : Promise<VirtualTourModel> {
        const path : string = `${BackendModel.base}/project/${project}/vt/${id}/`;
        const response : TVirtualTour = await super.delete<TVirtualTour>(path);
        const instance : VirtualTourModel = new VirtualTourModel(response);
        return instance;
    }
     // TODO change api to use pagination and return a paginator
    static async list(project: number) : Promise<Array<VirtualTourModel>> {
        const path : string = `${BackendModel.base}/project/${project}/vt/`;
        // TODO - this must be refactored
        // Array must be turned into a PaginatedList<model_class>
        const response : TVirtualTour = await super.get<TVirtualTour>(path);
        // this is a temporary hack
        const list = response['results'].map((responseProject) => {
           return new VirtualTourModel(responseProject);
        });
        return list;
    }
    async upload(file: XMLHttpRequestBodyInit, progress_handler?: ProgressCallback) {
        const upload_url : string = this.data.json_upload_url;
        if(!upload_url) {
            throw new BackendError('Virtual Tour NormaJSON+ upload URL unavailable');
        }
        const content_type : string = 'application/json';
        return BackendModel.upload(upload_url, content_type, file, progress_handler);
    }

    isCancellable(): boolean {
        return this.data.status < TRenderStatus.Canceled;
    }

    async cancel(): Promise<VirtualTourModel> {
        return VirtualTourModel.remove(this.data.project, this.data.id);
    }

    isCanceled(): boolean {
        return this.data.status == TRenderStatus.Canceled;
    }

    static async publicView(id: string) {
        const url = `${EnvConfig.getCacheBaseUrl()}public/vt/${id}/view.json`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new BackendError(response.statusText, response);
        }
        return await response.json();
    }

    static async view(id: string) {
        const url = `${VirtualTourModel.path}${id}/view/`;
        const token : string = await getToken();
        let command = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        };
        const response = await fetch(url, command);
        if (!response.ok) {
            throw new BackendError(response.statusText, response);
        }
        return await response.json();
    }

    async publish(publishAt: Date = null) {
        const date = publishAt ? publishAt.toISOString() : new Date().toISOString();
        const path : string = `${VirtualTourModel.path}${this.data.id}/publish/`;
        await BackendModel.post(path, {'publishAt': date});
        this.data.published_at = date;
    }

    async unpublish() {
        const path : string = `${VirtualTourModel.path}${this.data.id}/publish/`;
        await BackendModel.delete(path);
        this.data.published_at = '';
    }

    isPublished() : boolean {
        // This is null if coming from the BE
        return this.data.published_at?.length > 0;
    }

    getCreatedAt() : Date {
        return new Date(this.data.created_at);
    }

    getViewerURL() : string {
        let url = `/apps/home-viewer?vtid=${this.data.id}`;
        if(!EnvConfig.getIsReleaseMode())
            url += `&env=${EnvConfig.getEnvName()}`;
        return url;
    }

    isInProgress() : boolean {
        return this.data.status < TRenderStatus.Finished;
    }

    getProgress() : number {
        return this.data.progress * 100;
    }

    isPlaceholder() : boolean {
        return this.data.status == TRenderStatus.Placeholder;
    }

    isTerminal() : boolean {
        return this.data.status >= TRenderStatus.Canceled;
    }

    isCompleted() : boolean {
        return this.data.status == TRenderStatus.Finished;
    }

    isFailed() : boolean {
        return this.data.status == TRenderStatus.Failed;
    }

    notify(type: TNotificationType, callback?: TNotificationCallback) {
        NotificationModel.fromVirtualTour(this, type)
            .then((instance: NotificationModel) => {
                callback?.(instance);
            })
            .catch((reason) => {
                console.error(`Failed to Notify with Status ${TNotificationType[type]} for Virtual Tour ${this.data.id} in Project ${this.data.project}`, reason);
            });
    }
}
